import { OAuthCredentialsService } from '@tableau/oauth-credentials-service';
import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Filter } from '~commonInfra/utils/Filter';
import { Messages } from '~src/codegen/Localize';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IOAuthCredential, OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { learnMoreToken } from '~src/publishToSalesforce/components/PublishToSalesforceDialog';
import { SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ActivityLogSettings } from '~src/settings/IntegrationsSettings/activityLog/ActivityLogSettings';
import { ConnectToSalesforce, ConnectToSalesforceDropdown } from '~src/settings/IntegrationsSettings/components/ConnectToSalesforceDropdown';
import { SlackIntegrationSettings } from '~src/settings/IntegrationsSettings/slackConnectivity/SlackIntegrationSettings';
import {
  integrationSettingConnectButton,
  integrationSettingsContainer,
  integrationSpaceAuthenticateContainer,
  integrationSpaceContainer,
  integrationsSettingsPageHeader,
  salesforceSettingsAfterConnecting,
  salesforceSettingsGroupName,
  settingsGroupContainer,
  settingsSubsection
} from '~src/settings/IntegrationsSettings/styles/IntegrationsSettingsStyles';
import { SalesforceIntegrationAddCredentialsButtonClickEvent } from '~src/settings/IntegrationsSettings/telemetry/IntegrationsSettingsTelemetry';
import { IGetSiteSettingsForSiteAdminResult } from '~src/settings/ServerApi/requests/siteSettings/GetSiteSettingsForSiteAdminRequest';
import { IUpdateSiteSettingsForSiteAdminParams, IUpdateSiteSettingsForSiteAdminResult, UpdateSiteSettingsForSiteAdminRequest } from '~src/settings/ServerApi/requests/siteSettings/UpdateSiteSettingsForSiteAdminRequest';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { Sites } from '~src/siteManagement/services/resources/Sites';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ServerSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import {
  UploadBundlesDialogOpts
} from '~src/uploadBundles/components/ConnectedUploadBundlesDialog';
import { SiteRoles } from '~src/userManagement/users/serverApi/types/ISiteRole';

export const IntegrationsSubspaceName: ServerSettingsSubspaceName = 'integrations';

export interface IntegrationsSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isOnlineEnvironment: boolean;
  readonly userRole: String; // Prop added due to TFS 1320485
  readonly sites: Sites;
}

export type IntegrationsSiteSettingsSubspaceProps = IntegrationsSiteSettingsSubspaceOwnProps;

export interface IntegrationsSiteSettingsSubspaceState {
  readonly credentials: IOAuthCredential[];
  readonly siteSettings: IGetSiteSettingsForSiteAdminResult;
  readonly hasError: boolean;
  readonly publishToSalesforceChecked: boolean;
  readonly isSiteSettingsUpdateBusy: boolean;
}

export class IntegrationsSiteSettingsSubspaceComponent extends React.PureComponent<IntegrationsSiteSettingsSubspaceProps, IntegrationsSiteSettingsSubspaceState> {

  public constructor(props: IntegrationsSiteSettingsSubspaceProps) {
    super(props);
    this.state = {
      credentials: null,
      siteSettings: undefined,
      hasError: false,
      publishToSalesforceChecked: false,
      isSiteSettingsUpdateBusy: false
    };
  }

  public async componentDidMount() {
    await this.getOauthCredentials();
    await this.getSiteSettings();
  }

  async getOauthCredentials() {
    const credentialsQuery: ResourceQuery<IOAuthCredential> =
      OAuthCredentials.all().addFilter(Filter.fieldEq('type', OAuthCredentialTypes.Salesforce));

    const result = await SliceableHelper.fetchAll(credentialsQuery);
    if (_.isEmpty(result.errors)) {
      this.setState({ credentials: result.items });
    }
  }

  async getSiteSettings() {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      const siteAdminSettings: ISiteAdminSettings = siteSettings.siteAdminSettings as ISiteAdminSettings;
      this.setState({ siteSettings, publishToSalesforceChecked: siteAdminSettings.publishToSalesforceEnabled });
    } catch (e) {
      this.setState({ hasError: true });
    }
  }

  public handleSelectedCredentials = (credentialId: number): boolean => {
    ReactModalService.openCustom(UploadBundlesDialogOpts(credentialId));
    return true;
  };

  public handleConnectToSalesforce = (): void => {
    SalesforceIntegrationAddCredentialsButtonClickEvent();
    OAuthCredentialsService.AddCredential({
      bypassBeforeConnectingDialog: true,
      connectionType: OAuthCredentialTypes.Salesforce,
      onCancel: () => {
      }, // TODO: probably dont need to do anything.
      onCredentialAdded: this.handleOnCredentialsAdded,
      shouldKeepWindowOpen: false,
      sendEventFn: () => {
      },
      sessionLanguage: this.props.server.sessionLanguage(),
      sessionServerVersionBuild: this.props.server.sessionServerVersion().build,
      sessionServerVersionPlatform: this.props.server.sessionServerVersion().platform,
      connectionTypeDisplay: Messages.salesforce(),
      helpServiceExternalVersion: undefined,
      supportsGenericAuth: false,
    });
  };

  // credentialProperties is needed because we use it as a callback that requires it.
  handleOnCredentialsAdded = async (credentialId: string, _credentialProperties: [string, any]) => {
    this.handleSelectedCredentials(parseInt(credentialId));
    await this.getOauthCredentials(); // refresh state so button migrates to dropdown.
  };

  public render() {
    return (
      <div className={integrationSpaceContainer}>
        {this.shouldRenderSalesforceIntegrationSettings() && <div className={integrationSettingsContainer}>
          <h2 className={integrationsSettingsPageHeader}>{FeatureFlags.salesforceBundlesUIImprovements ? Messages.connectToSalesforce_SalesforceDataSourcesAndWorkbooksHeader()
            : Messages.salesforce()}</h2>
          {FeatureFlags.salesforceBundlesUIImprovements ? this.connectToSalesforceDataSegment() : this.connectToSalesforceSegment()}
          {FeatureFlags.salesforceBundlesUIImprovements ? this.salesforceAuthenticationSegment() : this.authenticationSegment()}
        </div>}
        {this.shouldRenderPublishToSalesforceSettings() && this.salesforcePublishToSalesforceSegment()}
        {FeatureFlags.userNotificationSlackChannel && <div className={integrationSettingsContainer}>
          <SlackIntegrationSettings />
        </div>}
        {FeatureFlags.ceppInitialReleaseEnabled && <div className={integrationSettingsContainer}>
          <ActivityLogSettings />
        </div>}
      </div>
    );
  }

  private shouldRenderSalesforceIntegrationSettings = (): boolean => {
    const isUserAuthorized: boolean = this.props.userRole === SiteRoles.ServerAdministrator || this.props.userRole === SiteRoles.SiteAdministratorCreator;
    return FeatureFlags.publishingSalesforceBundlesEnabled && isUserAuthorized;
  };

  private shouldRenderPublishToSalesforceSettings = (): boolean => {
    const isUserAuthorized: boolean = this.props.userRole === SiteRoles.SiteAdministratorExplorer || this.props.userRole === SiteRoles.SiteAdministratorCreator;
    return FeatureFlags.publishViewToSalesforce && isUserAuthorized && this.props.isOnlineEnvironment;
  };

  public connectToSalesforceSegment = (): JSX.Element => {
    return <div className={settingsGroupContainer}>
      <h3 className={salesforceSettingsGroupName}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksHeader()}
      </h3>
      <p className={settingsSubsection}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksFirstParagraph()}
      </p>
      <p className={settingsSubsection}>
        {Messages.connectToSalesforce_DataSourceAndWorkbooksEmbeddedCredentials()}
      </p>
      {this.getButton(this.state.credentials)}
      <p className={settingsSubsection}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksSecondParagraph()}
        {this.scheduleRefreshesTextWidget()}
      </p>
    </div>;
  };

  private scheduleRefreshesTextWidget = (): JSX.Element => {
    return <TextLinkWidget style={{ display: 'inline-block' }}
      testId={`${ConnectToSalesforce}-schedule-refreshes-learn-more`}
      kind={TextLinkKind.Standard}
      href={HelpService.getHelpUrlByCategory('schedule-refreshes')}
      target={'_blank'}
      rel={'noreferrer noopener'}>
      {Messages.connectToSalesforce_LearnMore()}
    </TextLinkWidget>;
  };

  public connectToSalesforceDataSegment = (): JSX.Element => {
    return <div className={settingsGroupContainer}>
      {<p className={settingsSubsection}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksExploreData()}
        <TextLinkWidget
          testId={`${ConnectToSalesforce}-create-workbooks-learn-more`}
          kind={TextLinkKind.Standard}
          href={HelpService.getHelpUrlByCategory('server-connect-salesforce')}
          target={'_blank'}
          rel={'noreferrer noopener'}>
          {Messages.connectToSalesforce_LearnMore()}
        </TextLinkWidget>
      </p>}
      <h3 className={salesforceSettingsGroupName}>
        {Messages.connectToSalesforce_getStarted()}
      </h3>
      <p className={settingsSubsection}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksParagraph()}
      </p>
      <p className={settingsSubsection}>
        {Messages.connectToSalesforce_DataSourceAndWorkbooksEmbedCredentials()}
      </p>
      {this.getButton(this.state.credentials)}
      <h3 className={salesforceSettingsAfterConnecting}>
        {Messages.connectToSalesforce_afterConnecting()}
      </h3>
      <div className={settingsSubsection} style={{ paddingBottom: 0 }}>
        <ul style={{ paddingLeft: 22, paddingBottom: 0 }}>
          <li>{Messages.connectToSalesforce_DataSourcesAndWorkbooksAfterConnectingBulletOne()}</li>
          <li style={{ whiteSpace: 'nowrap' }}>{Messages.connectToSalesforce_DataSourcesAndWorkbooksAfterConnectingBulletTwo()}
            {this.scheduleRefreshesTextWidget()}
          </li>
        </ul>
      </div>
    </div>;
  };

  public getButton = (credentials: IOAuthCredential[]): JSX.Element => {
    if (credentials && (credentials.length > 0)) {
      return <ConnectToSalesforceDropdown
        credentials={credentials}
        handleSelectedItem={this.handleSelectedCredentials}
        handleAddNewCredentials={this.handleConnectToSalesforce}
      />;
    }

    return <>
      <ButtonWidget
        testId={`${ConnectToSalesforce}-data`}
        className={integrationSettingConnectButton}
        id={`${ConnectToSalesforce}-data-button`}
        handleClick={this.handleConnectToSalesforce}
        buttonType={ButtonType.Outline}
      >
        {FeatureFlags.salesforceBundlesUIImprovements ? Messages.connectToSalesforce_Title() : Messages.connectToSalesforce_ConnectToSalesforceButtonTag()}
      </ButtonWidget>
    </>;
  };

  public authenticationSegment = (): JSX.Element => {
    if (this.props.isOnlineEnvironment) {
      return <div className={settingsGroupContainer}>
        <h3 className={salesforceSettingsGroupName}>
          {Messages.connectToSalesforce_AuthenticationHeader()}
        </h3>
        {this.authenticationBody()}
      </div>;
    }
  };

  public salesforceAuthenticationSegment = (): JSX.Element => {
    if (this.props.isOnlineEnvironment) {
      return <div>
        <div className={integrationSpaceAuthenticateContainer}>
          <h2 className={integrationsSettingsPageHeader}>
            {Messages.connectToSalesforce_salesforceAuthenticationAndIdentity()}
          </h2>
        </div>
        <div className={settingsGroupContainer}>
          {this.authenticationBody()}
        </div>
      </div>;
    }
  };

  private updateSiteSettingsForSiteAdminPublishToSalesforce(checked: boolean) {
    this.setState({ publishToSalesforceChecked: checked, isSiteSettingsUpdateBusy: true });
    const siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin = this.state.siteSettings;
    const siteAdminSettings: ISiteAdminSettings = siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings;
    siteAdminSettings.publishToSalesforceEnabled = checked;
    const params: IUpdateSiteSettingsForSiteAdminParams = {
      id: (siteSettingsForSiteAdmin as any).id,
      siteAdminSettings: siteAdminSettings,
      subscriptionSettings: siteSettingsForSiteAdmin.subscriptionSettings
    };

    try {
      WebHttpClient.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params)).then((result: IUpdateSiteSettingsForSiteAdminResult) => {
        if (result.errors) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.publishToSalesforceSiteSettingUpdateFailure() });
          this.setState({ publishToSalesforceChecked: !checked });
        } else {
          this.forceUpdate();
          getSessionManagementServiceInstance().refreshSessionInfo();
        }
        this.setState({ isSiteSettingsUpdateBusy: false });
      });
    } catch (e) {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.publishToSalesforceSiteSettingUpdateFailure() });
      this.setState({ publishToSalesforceChecked: !checked,  isSiteSettingsUpdateBusy: false });
    }
  }

  public salesforcePublishToSalesforceSegment = (): JSX.Element => {
    if (this.state.hasError) {
      return null;
    } else if (this.state.siteSettings) {
      return <div className={integrationSettingsContainer}>
        <h2 className={integrationsSettingsPageHeader}>
          {Messages.publishToSalesforceSiteSettingHeader()}
        </h2>
        <div className={settingsGroupContainer}>
          <div className={settingsSubsection}>
            <div className={settingsSubsection}>
              <span>{Messages.publishToSalesforceSiteSettingExplanation()}</span>
              <TextLinkWidget
                testId={`allow-publish-to-salesforce-learn-more`}
                kind={TextLinkKind.Standard}
                className={learnMoreStyle}
                href={HelpService.getHelpUrlByCategory(learnMoreToken)}
                target={'_blank'}
                rel={'noreferrer noopener'}>
                {Messages.publishToSalesforceDialogLearnMore()}
              </TextLinkWidget>
            </div>
            <CheckBoxWithLabelWidget
              label={Messages.publishToSalesforceSiteSettingExplanation()}
              checked={this.state.publishToSalesforceChecked}
              testId={'allow-publish-to-salesforce-checkbox'}
              removeLeftMargin={true}
              handleChange={this.updateSiteSettingsForSiteAdminPublishToSalesforce.bind(this)}
              disabled={this.state.isSiteSettingsUpdateBusy} />
          </div>
        </div>
      </div>;
    }
  };

  private authenticationBody = (): JSX.Element => {
    return <p className={settingsSubsection}>
      {Messages.connectToSalesforce_AuthenticationBody()}
      <TextLinkWidget
        testId={`${ConnectToSalesforce}-authentication`}
        kind={TextLinkKind.Standard}
        target={'_self'}
        href={this.props.urls.settingsAuthenticationUrl()}
      >
        {Messages.connectToSalesforce_AuthenticationLink()}
      </TextLinkWidget>
    </p>;
  };
}
